import { i18n } from '~/utils';

export const CONTACT_POINT_DIFFERENT_VALUES = {
  BRZ8_DLN_DOC_CLASS_ID: 'BRZ 8 Dokumentenklassen-ID',
  BRZ8_LOGIN_CONFIGURATION_NAME: 'BRZ 8 Login-Konfiguration',
  BRZ8_PASSWORD: 'BRZ 8 Passwort',
  BRZ8_SERVICE_URL: 'BRZ 8 Service-URL',
  BRZ8_USERNAME: 'BRZ 8 Benutzername',
  BRZ8_WSDL_URL: 'BRZ 8 WSDL-URL',
  COMPANY_ACCOUNT_ID: 'Firmenaccount',
  DATA_FORMAT: 'Dateityp',
  DATA_GROUPING: 'Gruppierung',
  DLN_FILENAME_PLACEHOLDER: 'Dateiname',
  DOCTYPE: 'PDF-Typ',
  EMAIL_RECEIVERS: 'E-Mail Empfänger',
  EMAIL_SUBJECT: 'Betreffzeile',
  ID: 'ID',
  INCLUDES_ATTACHMENTS: 'Anhänge einschließen',
  INTEGRATION_TYPE: 'Übertragungsweg',
  NAME: 'Name',
} as const;

/**
 * Mirrors the API's `DsContactPointIntegrationTypes`: a contact point on any
 * value it can store shows up in the data-subscription list and needs a label
 * here. Which of them the portal can create and edit is a separate, shorter
 * list — `INTEGRATION_TYPE_DEFINITIONS`.
 */
export const CONTACT_POINT_INTEGRATION_TYPE = {
  EMAIL: {
    KEY: 'email',
    STRING: 'E-Mail',
  },
  BRZ8: {
    KEY: 'brz8',
    STRING: 'BRZ 8 (DMS)',
  },
  // Generic Azure Storage Queue transport; the backend only accepts the
  // Nevaris JSON data format for it (used for the Nevaris/Klebl integration).
  AZURE_QUEUE: {
    KEY: 'azure_queue',
    STRING: 'Azure Queue',
  },
  RIBPES: {
    KEY: 'ribpes',
    STRING: 'RIB 4.0 (Leistungsermittlung)',
  },
  DOCUWARE: {
    KEY: 'docuware',
    STRING: 'DocuWare (DMS)',
  },
  API: {
    KEY: 'api',
    get STRING() {
      return i18n.t(($) => $.contactPoint.integrationTypes.api, {
        ns: 'admin',
      });
    },
  },
  DTRANS: {
    KEY: 'dtrans',
    STRING: 'DTrans',
  },
  // A format rather than a transport: the backend sends EDIFACT files over FTP.
  EDIFACT: {
    KEY: 'edifact',
    STRING: 'EDIFACT',
  },
  EUROVIA: {
    KEY: 'eurovia',
    STRING: 'Eurovia',
  },
  FOUR_PS: {
    KEY: 'four_ps',
    STRING: '4PS',
  },
  // Stored as `ftp`, but SFTP is the only protocol the API supports for it.
  FTP: {
    KEY: 'ftp',
    STRING: 'SFTP',
  },
  RIB: {
    KEY: 'rib',
    STRING: 'RIB 4.0 (Dokumentenablage)',
  },
  S3: {
    KEY: 's3',
    STRING: 'S3',
  },
  SCHLEITH: {
    KEY: 'schleith',
    STRING: 'Schleith',
  },
  SOFTLINE: {
    KEY: 'softline',
    STRING: 'SOFTLINE (Lieferscheinimport)',
  },
} as const;

export const CONTACT_POINT_DATA_FORMAT = {
  // Only valid for the azure_queue integration; hidden from the free
  // data-format selection (see INTEGRATION_SPECIFIC_DATA_FORMATS).
  AZURE_QUEUE_NEVARIS: {
    KEY: 'azure_queue_nevaris',
    STRING: 'Azure Queue Nevaris',
  },
  EINS_LIEFERSCHEIN: {
    KEY: 'eins_lieferschein',
    STRING: '1Lieferschein (als Anhang)',
  },
  JSON: {
    KEY: 'json',
    get STRING() {
      return i18n.t(($) => $.contactPoint.dataFormats.json, { ns: 'admin' });
    },
  },
  PDF: {
    KEY: 'pdf',
    STRING: 'PDF (als Anhang)',
  },
  PDF_JSON: {
    KEY: 'pdf_json',
    STRING: 'PDF und JSON (als Anhang)',
  },
  UGL: {
    KEY: 'ugl',
    STRING: 'UGL (als Anhang)',
  },
  ZIP: {
    KEY: 'zip',
    STRING: 'PDF in zip (als Anhang)',
  },
  ZIP_JSON: {
    KEY: 'zip_json',
    STRING: 'JSON in zip (als Anhang)',
  },
  ZIP_PDF_JSON: {
    KEY: 'zip_pdf_json',
    STRING: 'PDF und JSON in zip (als Anhang)',
  },
} as const;

export const CONTACT_POINT_DATA_GROUPING = {
  COMBINED: {
    KEY: 'combined',
    STRING: 'Alle Lieferscheine in einer Datei',
  },
  NO_GROUPING: {
    KEY: 'no_grouping',
    STRING: 'Jeder Lieferschein als eigene Datei',
  },
} as const;

export const DATA_SUBSCRIPTION_DIFFERENT_VALUES = {
  ACTIVE: 'Aktiv',
  COMPANY_ACCOUNT_ID: 'Firmenaccount',
  DATE_FILTER_CLAUSES: 'Filterklauseln (Zeitraum)',
  FILTER_CLAUSES: 'Filterklauseln',
  FILTER_UNION: 'und/oder',
  ID: 'ID',
  NAME: 'Name',
  SUBSCRIPTION_EVENTS: 'Events',
  TRIGGER: 'Auslöser',
} as const;

export const DATA_SUBSCRIPTION_TRIGGER = {
  PROCESS_STATE_CHANGE: {
    DESCRIPTION:
      'Wird ausgelöst, wenn sich der Status (Ausgabe, Geliefert, ...) eines Lieferscheins ändert.',
    KEY: 'process_state_change',
    STRING: 'Statusänderung',
  },
} as const;

// oxlint-disable-next-line sort-keys -- the picker lists them in this order
export const DATA_SUBSCRIPTION_EVENT = {
  IMPORTED: {
    KEY: 'imported',
    get STRING() {
      return i18n.t(($) => $.dataSubscription.events.imported, { ns: 'admin' });
    },
  },
  READY_FOR_OUTPUT: {
    KEY: 'readyForOutput',
    get STRING() {
      return i18n.t(($) => $.dataSubscription.events.readyForOutput, {
        ns: 'admin',
      });
    },
  },
  IN_TRANSPORT: {
    KEY: 'inTransport',
    get STRING() {
      return i18n.t(($) => $.dataSubscription.events.inTransport, {
        ns: 'admin',
      });
    },
  },
  ARRIVED: {
    KEY: 'arrived',
    get STRING() {
      return i18n.t(($) => $.dataSubscription.events.arrived, { ns: 'admin' });
    },
  },
  DELIVERED: {
    KEY: 'delivered',
    get STRING() {
      return i18n.t(($) => $.dataSubscription.events.delivered, {
        ns: 'admin',
      });
    },
  },
  BILLED: {
    KEY: 'billed',
    get STRING() {
      return i18n.t(($) => $.dataSubscription.events.billed, { ns: 'admin' });
    },
  },
  CANCELLED: {
    KEY: 'cancelled',
    get STRING() {
      return i18n.t(($) => $.dataSubscription.events.cancelled, {
        ns: 'admin',
      });
    },
  },
} as const;

export const DATA_SUBSCRIPTION_FILTER_UNION = {
  AND: {
    KEY: 'and',
    STRING: 'und',
  },
  OR: {
    KEY: 'or',
    STRING: 'oder',
  },
} as const;

export const DATA_SUBSCRIPTION_DATE_FILTER_CLAUSE_FILTER_KEYWORD = {
  CREATION_DATE_FROM: {
    KEY: 'dln_date_created_from',
    STRING: 'LFS-Erstellungsdatum von',
  },
  CREATION_DATE_TO: {
    KEY: 'dln_date_created_to',
    STRING: 'LFS-Erstellungsdatum bis',
  },
  DLN_DATE_FROM: {
    KEY: 'dln_date_from',
    STRING: 'LFS-Datum von',
  },
  DLN_DATE_TO: {
    KEY: 'dln_date_to',
    STRING: 'LFS-Datum bis',
  },
  MODIFIED_DATE_FROM: {
    KEY: 'dln_date_modified_from',
    STRING: 'LFS-Änderungsdatum von',
  },
  MODIFIED_DATE_TO: {
    KEY: 'dln_date_modified_to',
    STRING: 'LFS-Änderungsdatum bis',
  },
} as const;

export const DATA_SUBSCRIPTION_DATE_FILTER_CLAUSE_VALUE_TYPE = 'timedelta';
export const DATA_SUBSCRIPTION_FILTER_CLAUSE_VALUE_TYPE = 'json';

export const DATA_SUBSCRIPTION_FILTER_CLAUSE_FILTER_KEYWORD = {
  BUYER_ID: {
    KEY: 'buyer_id',
    STRING: 'Auftraggeber (ID)',
  },
  BUYER_NAME: {
    KEY: 'buyer_name',
    STRING: 'Auftraggeber (Name)',
  },
  CUSTOMER_NUMBER: {
    KEY: 'customer_number',
    STRING: 'Kundennummer',
  },
  DLN_NR: {
    KEY: 'dln_nr',
    STRING: 'LFS-Nr.',
  },
  SELLER_ID: {
    KEY: 'seller_id',
    STRING: 'Auftragnehmer (ID)',
  },
  SELLER_NAME: {
    KEY: 'seller_name',
    STRING: 'Auftragnehmer (Name)',
  },
  SHIP_TO_ID: {
    KEY: 'ship_to_id',
    STRING: 'Abnehmer (ID)',
  },
  SHIP_TO_NAME: {
    KEY: 'ship_to_name',
    STRING: 'Abnehmer (Name)',
  },
  SHIP_TO_SITE_ID: {
    KEY: 'ship_to_site_id',
    STRING: 'Lieferort (ID)',
  },
  SUPPLIER_ASSIGNED_SITE: {
    KEY: 'supplier_assigned_site',
    STRING: 'Lieferort (Lieferantenbez.) (Name)',
  },
  SUPPLIER_ID: {
    KEY: 'supplier_id',
    STRING: 'Lieferant (ID)',
  },
  SUPPLIER_NAME: {
    KEY: 'supplier_name',
    STRING: 'Lieferant (Name)',
  },
} as const;

type FilterKeyword = typeof DATA_SUBSCRIPTION_FILTER_CLAUSE_FILTER_KEYWORD;
export type FilterKey = FilterKeyword[keyof FilterKeyword]['KEY'];

type FilterUnion = typeof DATA_SUBSCRIPTION_FILTER_UNION;
export type FilterUnionKey = FilterUnion[keyof FilterUnion]['KEY'];
